<script setup>
import {
  computed,
  onMounted,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { v7 as uuidv7 } from 'uuid';
import Swiper from 'swiper';
import ImageComponent from '@/components/common/ImageComponent.vue';
import PreviewHeader from '@/components/preview/kakao/common/PreviewHeader.vue';
import PreviewFooter from '@/components/preview/kakao/common/PreviewFooter.vue';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
import { COUPON_TYPE_OPTIONS } from '@/config/constant';
import ConsentInformation from './ConsentInformation.vue';

let swiper;
const swiperElement = ref(null);

const props = defineProps({
  brandCarouselFeed: {
    type: Object,
    default: () => ({}),
  },
  position: {
    type: String,
    default: '',
  },
  kakaoProfile: {
    type: String,
    default: '',
  },
  swipeEnabled: {
    type: Boolean,
    default: false,
  },
});

const {
  brandCarouselFeed,
  position,
  swipeEnabled,
} = toRefs(props);
const { t } = useI18n();
const id = uuidv7();

const couponType = COUPON_TYPE_OPTIONS.reduce((current, item) => ({
  [`${item.type}`]: item.text,
  ...current,
}), {});

const tailClass = computed(() => {
  if (!brandCarouselFeed.value.tail?.enabled) {
    return 'd-none';
  }

  return position.value === 'carousel-feed-tail' ? 'editAreaPositionHover' : '';
});

const displayList = computed(() => (brandCarouselFeed.value?.list || [])
  .map(({
    header,
    content,
    buttons,
    coupon = {},
    tail = {},
    image = {},
  }, i) => {
    const {
      type,
      name: title,
      description,
    } = coupon;

    const str = t(couponType[type] ?? 'empty');
    const couponTitle = type === 'DELIVERY_DISCOUNT' ? str : `${title}${str.slice(2)}`;

    const classList = [];

    if (!coupon?.enabled) {
      classList.push('d-none');
    }

    if (position.value === `carousel-feed-${i}-coupon`) {
      classList.push('editAreaPositionHover');
    }

    const itemHover = position.value === `carousel-feed-${i}`;
    const imageHover = position.value === `carousel-feed-${i}-image`;
    const titleHover = position.value === `carousel-feed-${i}-header`;
    const contentHover = position.value === `carousel-feed-${i}-content`;

    const tempButtons = (buttons ?? []).map(({ name }) => ({ name }));

    if (!tempButtons.length) {
      tempButtons.push({ name: t('placeholder.button') });
    }

    const buttonsHover = position.value === `carousel-feed-${i}-buttons` && tempButtons?.length;

    return {
      imageUrl: image?.url,
      header: header || t('kakaoBasic.preview.title'),
      contentList: (content || t('kakaoBasic.preview.content'))
        .split('\n')
        .map((v) => v || '\u00A0'),
      buttons: tempButtons,
      coupon: {
        enabled: coupon?.enabled,
        title: couponTitle,
        description,
      },
      tailEnabled: tail.enabled,
      itemClass: itemHover ? 'editAreaPositionHover' : '',
      imageClass: imageHover ? 'editAreaPositionHover' : '',
      titleClass: titleHover ? 'editAreaPositionHover' : '',
      contentClass: contentHover ? 'editAreaPositionHover' : '',
      buttonsClass: buttonsHover ? 'editAreaPositionHover' : '',
      couponClass: classList.join(' '),
    };
  }));

onMounted(() => {
  swiper = new Swiper(swiperElement.value, {
    slidesPerView: 'auto',
    spaceBetween: 15,
    // observer: true,
    // observeParents: true,
    allowTouchMove: false,
  });

  watch(swipeEnabled, (v) => {
    swiperElement.value.swiper.allowSlideNext = v;
    swiperElement.value.swiper.allowSlidePrev = v;
    swiperElement.value.swiper.allowTouchMove = v;
  }, { immediate: true });

  swiper.slideTo(brandCarouselFeed.value.index, 0);

  watch(() => brandCarouselFeed.value.index, (v) => swiper.slideTo(v));
});

defineOptions({
  inheritAttrs: false,
});

</script>
<template>
  <div class="preview-talk preview-talk--active">
    <ConsentInformation />
    <div class="preview-inner mockup-scroll">
      <PreviewHeader
        :kakao-profile="kakaoProfile"
      />
      <div
        ref="swiperElement"
        class="preview-message preview-message__swiper carousel-feed preview-message--active"
      >
        <ul class="swiper-wrapper">
          <li
            v-for="(item, i) in displayList"
            :key="`preview-${id}-${i}`"
            class="swiper-slide"
            :class="item.itemClass"
          >
            <div class="preview-message__container">
              <div class="preview-message__content">
                <div
                  class="preview-message__inner-img"
                  :class="item.imageClass"
                >
                  <ImageComponent
                    :src="item?.imageUrl"
                    :alt="`carousel-image-${i}`"
                  />
                </div>
                <div class="preview-message__inner">
                  <h5
                    class="carousel-feed__title"
                    :class="item.titleClass"
                  >{{ item.header }}</h5>
                  <div
                    class="carousel-feed__desc"
                    :class="item.contentClass"
                  >
                    <p
                      v-for="(text, j) in item.contentList"
                      :key="`preview-carouselFeed-${i}-content-${j}`"
                    >{{ text }}</p>
                  </div>
                </div>
                <div
                  class="preview-message__button preview-message__button--horizontal"
                  :class="item.buttonsClass"
                >
                  <button
                    v-for="(elm, j) in item.buttons"
                    :key="`preview-carouselFeed-${i}-button-${j}`"
                    type="button"
                    :class="{
                      'preview-message__button--kakao': elm.type === 'AC',
                      'preview-message__button--yellow': elm.type === 'BF',
                    }"
                  >
                    {{ elm.name }}
                  </button>
                </div>
                <div class="preview-message__coupon">
                  <button
                    type="button"
                    :class="item.couponClass"
                  >
                    <div class="coupon__text">
                      <h5 class="coupon__title">{{ item.coupon.title }}</h5>
                      <p class="coupon__desc">{{ item.coupon.description }}</p>
                    </div>
                    <div class="coupon__icon">
                      <i></i>
                      <span>COUPON</span>
                    </div>
                  </button>
                </div>
                <PreviewFooter />
              </div>
            </div>
          </li>
          <div
            class="swiper-slide carousel-feed__more-view carousel-remove--is-active"
            :class="tailClass"
          >
            <div class="more-view">
              <a>
                <i></i>
                <span>{{ t('kakaoBasic.preview.tail') }}</span>
              </a>
            </div>
          </div>
        </ul>
      </div>
    </div>
  </div>
</template>
<style>
</style>
